import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Category,
  Image,
  VolumeUp,
  Logout,
  AccountTree,
  People,
  Business,
  Security,
  Storage,
  Chat as ChatIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { AdminDashboard } from './AdminDashboard';
import { CategoriesPage } from '../pages/Categories';
import { ItemsPage } from '../pages/Items';
import HierarchyLevelsPage from '../pages/HierarchyLevels';
import Sources from '../pages/Sources';
import Roles from '../pages/Roles';
import Tenants from '../pages/Tenants';
import Users from '../pages/Users';
import Voices from '../pages/Voices';
import { User } from '../types';
import { usePresence, AdminMessage } from '../hooks/usePresence';

interface AdminLayoutProps {
  user: User | null;
  onSwitchToPortal?: () => void;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ user, onSwitchToPortal }) => {
  const { logout } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);

  const isSuperAdmin = user?.roleId === 1;
  const [snackMsg, setSnackMsg] = useState<AdminMessage | null>(null);
  const handleMessage = useCallback((msg: AdminMessage) => {
    setSnackMsg(msg);
    // Show a browser notification when the tab is not in focus
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification('TalkForTy', {
          body: msg.message,
          icon: '/assets/images/TalkForTy-icon.png',
          tag: 'talkforty-notification',
        });
      } catch {
        // Browser Notification API not available in this context
      }
    }
  }, []);

  // Request browser notification permission when the user has SignalR notifications enabled
  useEffect(() => {
    if (
      user?.notificationPreferences?.signalREnabled &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      Notification.requestPermission().catch(() => undefined);
    }
  }, [user?.notificationPreferences?.signalREnabled]);
  // Connect all admin users to presence (not just super-admins), so they appear
  // online in the super-admin dashboard. The hub itself controls what each role receives.
  const { connectedUsers, isConnected, error: presenceError, sendMessage } = usePresence(user?.roleId !== undefined, handleMessage);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const tabs = [
    { label: 'Dashboard', icon: <DashboardIcon />, component: <AdminDashboard user={user} onNavigateToTab={setCurrentTab} connectedUsers={connectedUsers} isConnected={isConnected} presenceError={presenceError} sendMessage={sendMessage} /> },
    { label: 'Categories', icon: <Category />, component: <CategoriesPage /> },
    { label: 'Items', icon: <Image />, component: <ItemsPage /> },
    { label: 'Hierarchy Levels', icon: <AccountTree />, component: <HierarchyLevelsPage /> },
    { label: 'Users', icon: <People />, component: <Users /> },
    { label: 'Roles', icon: <Security />, component: <Roles /> },
    { label: 'Tenants', icon: <Business />, component: <Tenants /> },
    { label: 'Sources', icon: <Storage />, component: <Sources /> },
    { label: 'Voices', icon: <VolumeUp />, component: <Voices /> },
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Unified Header with Logo, User Menu, and Navigation Tabs */}
      <AppBar position="static" elevation={1} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
        <Container maxWidth="lg">
          {/* First Row - Logo, Title, and User Menu */}
          <Toolbar sx={{ px: 0, minHeight: '64px !important' }}>
            {/* Left side - Logo and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <img 
                src="/assets/images/TalkForTy-icon.png" 
                alt="TalkForTy Logo" 
                className="logo-smooth"
                style={{ 
                  height: '40px', 
                  width: '40px',
                  marginRight: '12px',
                  objectFit: 'contain'
                }} 
              />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                TalkForTy Control Panel
              </Typography>
            </Box>
            
            {/* Right side - User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>
                {user?.displayName || user?.emailAddress || 'Control Panel User'}
              </Typography>
              <IconButton onClick={handleMenuClick}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user?.displayName?.charAt(0) || user?.emailAddress?.charAt(0) || 'C'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {onSwitchToPortal && (
                  <MenuItem onClick={() => { handleMenuClose(); onSwitchToPortal(); }}>
                    <AccountCircleIcon sx={{ mr: 1 }} />
                    My Account
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={logout}>
                  <Logout sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
          
          {/* Second Row - Navigation Tabs */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                minHeight: '48px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: '48px',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main'
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
        </Container>
      </AppBar>

      {/* Content */}
      <Box>
        {tabs[currentTab]?.component}
      </Box>

      {/* Admin message notification – shown on all tabs */}
      <Snackbar
        open={Boolean(snackMsg)}
        autoHideDuration={8000}
        onClose={() => setSnackMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          icon={<ChatIcon />}
          onClose={() => setSnackMsg(null)}
          sx={{ width: '100%', maxWidth: 400 }}
        >
          <Typography variant="subtitle2">
            {snackMsg?.fromUserId === user?.id
              ? `Message delivered to ${snackMsg?.toDisplayName}`
              : `Message from ${snackMsg?.fromDisplayName}`}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            "{snackMsg?.message}"
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Coming Soon Component
const ComingSoon: React.FC<{ title: string }> = ({ title }) => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          This section is coming soon! We're working hard to bring you these features.
        </Typography>
        <Button variant="contained" disabled>
          Under Development
        </Button>
      </Paper>
    </Container>
  );
};
